import React, { useState } from "react";
import axios from "axios";
import { CONTACT } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Mail, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ContactSection = () => {
  const [form, setForm] = useState({ name: "", email: "", organisation: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submitContact = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please complete name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        organisation: form.organisation || null,
        message: form.message,
      });
      toast.success("Thank you — your message has reached VegUK.");
      setForm({ name: "", email: "", organisation: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const subscribe = async (e) => {
    e.preventDefault();
    if (!newsletterEmail.trim()) {
      toast.error("Please enter an email address.");
      return;
    }
    setSubscribing(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email: newsletterEmail });
      toast.success("Subscribed. Welcome to the VegUK list.");
      setNewsletterEmail("");
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) toast.message("You are already subscribed.");
      else toast.error("Could not subscribe. Please try again.");
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid={CONTACT.root}
      className="py-24 lg:py-32 bg-[#F9F6F0]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-5">
          <span className="veg-overline">Get in touch</span>
          <h2 className="font-serif-display mt-4 text-4xl sm:text-5xl text-[#2E5C46] leading-[1.05]">
            Questions, clarifications or a request for our definitions?
          </h2>
          <p className="mt-6 text-[#4A5D4E] leading-relaxed">
            VegUK welcomes enquiries from caterers, care homes, schools, hospitals,
            airlines, restaurants, councils and individuals. We provide our definitions
            free of charge and assist with implementation.
          </p>
          <a
            href="mailto:Info@VegetarianUK.org"
            className="mt-8 inline-flex items-center gap-3 text-[#2E5C46] hover:text-[#234836] transition-colors"
          >
            <Mail size={18} />
            <span className="border-b border-[#2E5C46]/40 pb-0.5">
              Info@VegetarianUK.org
            </span>
          </a>

          {/* Newsletter */}
          <div className="mt-12 p-7 bg-white border border-[#D1C9B9] rounded-lg">
            <h3 className="font-serif-display text-2xl text-[#2C352D] mb-2">
              Newsletter
            </h3>
            <p className="text-sm text-[#4A5D4E] mb-5 leading-relaxed">
              Receive updates as the definitions, partners and guidance evolve.
            </p>
            <form onSubmit={subscribe} className="flex gap-2">
              <Input
                data-testid={CONTACT.newsletterEmail}
                type="email"
                required
                placeholder="you@example.com"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                className="bg-[#F9F6F0] border-[#D1C9B9] focus-visible:ring-[#2E5C46] text-[#2C352D]"
              />
              <Button
                data-testid={CONTACT.newsletterSubmit}
                type="submit"
                disabled={subscribing}
                className="bg-[#2E5C46] hover:bg-[#234836] text-[#F9F6F0] rounded-md shrink-0"
              >
                {subscribing ? "…" : "Subscribe"}
              </Button>
            </form>
          </div>
        </div>

        <div className="lg:col-span-7">
          <form
            onSubmit={submitContact}
            className="bg-white border border-[#D1C9B9] rounded-lg p-8 lg:p-10 space-y-6"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="name" className="text-[#2C352D]">Name</Label>
                <Input
                  id="name"
                  name="name"
                  data-testid={CONTACT.formName}
                  required
                  value={form.name}
                  onChange={onChange}
                  className="mt-2 bg-[#F9F6F0] border-[#D1C9B9] focus-visible:ring-[#2E5C46] text-[#2C352D]"
                  placeholder="Your full name"
                />
              </div>
              <div>
                <Label htmlFor="email" className="text-[#2C352D]">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  data-testid={CONTACT.formEmail}
                  required
                  value={form.email}
                  onChange={onChange}
                  className="mt-2 bg-[#F9F6F0] border-[#D1C9B9] focus-visible:ring-[#2E5C46] text-[#2C352D]"
                  placeholder="you@organisation.org"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="organisation" className="text-[#2C352D]">
                Organisation <span className="text-[#4A5D4E] text-xs">(optional)</span>
              </Label>
              <Input
                id="organisation"
                name="organisation"
                data-testid={CONTACT.formOrg}
                value={form.organisation}
                onChange={onChange}
                className="mt-2 bg-[#F9F6F0] border-[#D1C9B9] focus-visible:ring-[#2E5C46] text-[#2C352D]"
                placeholder="School, hospital, restaurant…"
              />
            </div>
            <div>
              <Label htmlFor="message" className="text-[#2C352D]">Message</Label>
              <Textarea
                id="message"
                name="message"
                data-testid={CONTACT.formMessage}
                required
                rows={6}
                value={form.message}
                onChange={onChange}
                className="mt-2 bg-[#F9F6F0] border-[#D1C9B9] focus-visible:ring-[#2E5C46] text-[#2C352D] resize-none"
                placeholder="How can VegUK help you?"
              />
            </div>
            <Button
              data-testid={CONTACT.formSubmit}
              type="submit"
              disabled={submitting}
              className="bg-[#2E5C46] hover:bg-[#234836] text-[#F9F6F0] rounded-md px-7 py-6"
            >
              {submitting ? (
                "Sending…"
              ) : (
                <span className="inline-flex items-center gap-2">
                  Send message <Send size={16} />
                </span>
              )}
            </Button>
          </form>
        </div>
      </div>
    </section>
  );
};
